"use client";

import { useState, useMemo, useEffect } from "react";
import { Button, CloseIcon, BackIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, SunIcon, MoonIcon, LockIcon, UnlockIcon } from "./Button";
import { adjustColorLightness, hexToRgbString, THEME_ADJUSTMENT } from "@/utils/colors";
import { Magnetic } from "./Magnetic";
import { StatBlock } from "./StatBlock";
import { MiniCube } from "./MiniCube";

interface StyleGuideProps {
  isOpen: boolean;
  onClose: () => void;
  isLightMode?: boolean;
  overrideTheme?: { base: string; bright: string; hue: number } | null;
}

// Section wrapper for consistent styling
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-12">
      <h2 className="text-[length:var(--btn-font-size)] font-bold uppercase tracking-[0.15em] text-[rgba(var(--foreground-rgb),0.5)] mb-6">
        {title}
      </h2>
      {children}
    </div>
  );
}

// Subsection for grouping related items
function Subsection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.4)] mb-3 uppercase tracking-[0.1em]">
        {label}
      </div>
      {children}
    </div>
  );
}

// Token display component
function TokenRow({ name, value, preview }: { name: string; value: string; preview?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[rgba(var(--foreground-rgb),0.1)]">
      <code className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.7)] font-mono">{name}</code>
      <div className="flex items-center gap-3">
        <code className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] font-mono">{value}</code>
        {preview}
      </div>
    </div>
  );
}

export function StyleGuide({ isOpen, onClose, isLightMode = false, overrideTheme }: StyleGuideProps) {
  const [activeTab, setActiveTab] = useState<string>("buttons");

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock scroll when open
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  // Compute theme colors (static, not lerped)
  const themeColors = useMemo(() => {
    const adjustment = isLightMode ? THEME_ADJUSTMENT.light : THEME_ADJUSTMENT.dark;

    if (overrideTheme) {
      const base = adjustColorLightness(overrideTheme.base, adjustment);
      const bright = adjustColorLightness(overrideTheme.bright, adjustment);
      return {
        fg: hexToRgbString(base),
        fgHex: base,
        base: hexToRgbString(base),
        baseHex: base,
        bright: hexToRgbString(bright),
        brightHex: bright,
      };
    }

    // Neutral theme - use foreground color
    const fgVal = isLightMode ? "10, 10, 10" : "255, 255, 255";
    const fgHex = isLightMode ? "#0a0a0a" : "#ffffff";
    return {
      fg: fgVal,
      fgHex: fgHex,
      base: fgVal,
      baseHex: fgHex,
      bright: fgVal,
      brightHex: fgHex,
    };
  }, [overrideTheme, isLightMode]);

  if (!isOpen) return null;

  const tabs = [
    { id: "buttons", label: "Buttons" },
    { id: "glass", label: "Glass" },
    { id: "typography", label: "Type" },
    { id: "tokens", label: "Tokens" },
    { id: "icons", label: "Icons" },
    { id: "components", label: "Components" },
    { id: "changelog", label: "Changelog" },
  ];

  const DS_VERSION = "1.0.0";

  // Static colors for StyleGuide (not affected by theme lerping)
  const fg = isLightMode ? "10, 10, 10" : "255, 255, 255";
  const bg = isLightMode ? "245, 245, 245" : "10, 10, 10";

  return (
    <div
      className="fixed inset-0 z-[300] overflow-auto"
      data-lenis-prevent
      data-style-guide
      style={{
        backgroundColor: `rgb(${bg})`,
        // Override lerped CSS variables with static values
        '--foreground': `rgb(${fg})`,
        '--foreground-rgb': fg,
        '--background': `rgb(${bg})`,
        '--background-rgb': bg,
        '--theme-fg': themeColors.fgHex,
        '--theme-fg-rgb': themeColors.fg,
        '--theme-base': themeColors.baseHex,
        '--theme-base-rgb': themeColors.base,
        '--theme-bright': themeColors.brightHex,
        '--theme-bright-rgb': themeColors.bright,
      } as React.CSSProperties}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 px-6 py-4 border-b border-[rgba(var(--foreground-rgb),0.1)] backdrop-blur-xl bg-[rgba(var(--background-rgb),0.8)]">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-headline text-[var(--foreground)]">Mission Control</h1>
            <p className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)]">Design System v{DS_VERSION}</p>
          </div>
          <Button variant="icon" onClick={onClose} aria-label="Close styleguide">
            <CloseIcon />
          </Button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="sticky top-[73px] z-10 px-6 py-3 border-b border-[rgba(var(--foreground-rgb),0.1)] backdrop-blur-xl bg-[rgba(var(--background-rgb),0.8)]">
        <div className="max-w-4xl mx-auto flex gap-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-[0.8rem] font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-[var(--foreground)] text-[var(--background)]"
                  : "text-[rgba(var(--foreground-rgb),0.6)] hover:text-[var(--foreground)] hover:bg-[rgba(var(--foreground-rgb),0.1)]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Buttons Section */}
        {activeTab === "buttons" && (
          <>
            <Section title="Button Variants">
              <Subsection label="Primary">
                <div className="flex flex-wrap gap-4">
                  <Button variant="primary">Primary Button</Button>
                  <Button variant="primary" disabled>Disabled</Button>
                </div>
              </Subsection>

              <Subsection label="Secondary">
                <div className="flex flex-wrap gap-4">
                  <Button variant="secondary">Secondary Button</Button>
                  <Button variant="secondary" disabled>Disabled</Button>
                </div>
              </Subsection>

              <Subsection label="Icon Buttons">
                <div className="flex flex-wrap gap-4 items-center">
                  <Button variant="icon" aria-label="Close">
                    <CloseIcon />
                  </Button>
                  <Button variant="icon" aria-label="Back">
                    <BackIcon size={20} />
                  </Button>
                  <Button variant="icon-sm" aria-label="Previous">
                    <ChevronLeftIcon />
                  </Button>
                  <Button variant="icon-sm" aria-label="Next">
                    <ChevronRightIcon />
                  </Button>
                </div>
              </Subsection>
            </Section>

            <Section title="Magnetic Effect">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Move your cursor near buttons to see the magnetic attraction effect.
              </p>
              <div className="flex gap-8">
                <div className="text-center">
                  <Button variant="primary">Magnetic On</Button>
                  <p className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] mt-2">Default</p>
                </div>
                <div className="text-center">
                  <Button variant="primary" magnetic={false}>Magnetic Off</Button>
                  <p className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] mt-2">magnetic=false</p>
                </div>
              </div>
            </Section>
          </>
        )}

        {/* Glass Section */}
        {activeTab === "glass" && (
          <>
            <Section title="Glass Effect">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Unified glassmorphism with tapered rim light border. One class, consistent look.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div
                  className="glass no-hover p-6 rounded-2xl"
                  style={{
                    isolation: "isolate",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                  }}
                >
                  <div className="font-medium text-[var(--foreground)] mb-1">Static</div>
                  <code className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">.glass.no-hover</code>
                  <p className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] mt-3">
                    No hover effect. Use for containers that shouldn&apos;t react to mouse.
                  </p>
                </div>
                <div
                  className="glass p-6 rounded-2xl cursor-pointer"
                  style={{
                    isolation: "isolate",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                  }}
                >
                  <div className="font-medium text-[var(--foreground)] mb-1">Interactive</div>
                  <code className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">.glass</code>
                  <p className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] mt-3">
                    Hover to see the rim light brighten. Default behavior for buttons and clickable elements.
                  </p>
                </div>
              </div>
            </Section>

            <Section title="Inline Styles Required">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Due to browser bugs with <code className="text-[0.75rem]">backdrop-filter</code> in stacking contexts, always add inline blur styles:
              </p>
              <div className="glass p-4 rounded-xl no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <pre className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.7)] font-mono overflow-x-auto">
{`style={{
  isolation: "isolate",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
}}`}
                </pre>
              </div>
            </Section>
          </>
        )}

        {/* Typography Section */}
        {activeTab === "typography" && (
          <>
            <Section title="Type Scale (Tokenized)">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                All sizes use <code className="text-[0.75rem]">clamp()</code> for fluid scaling.
              </p>
              <div className="space-y-6">
                {[
                  { token: "--text-4xl", value: "clamp(2.75rem, 7vw, 6rem)", label: "Display" },
                  { token: "--text-3xl", value: "clamp(2.25rem, 6vw, 5rem)", label: "Hero" },
                  { token: "--text-2xl", value: "clamp(1.75rem, 5vw, 4rem)", label: "H1" },
                  { token: "--text-xl", value: "clamp(1.25rem, 3vw, 1.75rem)", label: "H2" },
                  { token: "--text-lg", value: "clamp(1.1rem, 2.2vw, 1.375rem)", label: "H3" },
                  { token: "--text-base", value: "clamp(0.9rem, 1.8vw, 1.1rem)", label: "Body" },
                  { token: "--text-sm", value: "clamp(0.8rem, 1.5vw, 0.95rem)", label: "Small" },
                  { token: "--text-xs", value: "clamp(0.7rem, 1.2vw, 0.8rem)", label: "Caption" },
                ].map((item) => (
                  <div key={item.token} className="flex items-baseline gap-4 pb-4 border-b border-[rgba(var(--foreground-rgb),0.1)]">
                    <span style={{ fontSize: `var(${item.token})` }} className="font-headline text-[var(--foreground)]">Aa</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <code className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.7)] font-mono">{item.token}</code>
                        <span className="text-[var(--foreground)] font-medium">• {item.label}</span>
                      </div>
                      <code className="text-[0.65rem] text-[rgba(var(--foreground-rgb),0.4)] font-mono">{item.value}</code>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Line Heights">
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--leading-tight" value="1.1" />
                <TokenRow name="--leading-snug" value="1.25" />
                <TokenRow name="--leading-normal" value="1.5" />
                <TokenRow name="--leading-relaxed" value="1.7" />
                <TokenRow name="--leading-loose" value="1.9" />
              </div>
            </Section>

            <Section title="Font Weights">
              <div className="space-y-3">
                <p className="font-normal text-[var(--foreground)]">Normal (400) - Body text</p>
                <p className="font-medium text-[var(--foreground)]">Medium (500) - Buttons, labels</p>
                <p className="font-bold text-[var(--foreground)]">Bold (700) - Headlines</p>
              </div>
            </Section>
          </>
        )}

        {/* Tokens Section */}
        {activeTab === "tokens" && (
          <>
            <Section title="Button Tokens">
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--btn-scale" value="1.05" />
                <TokenRow name="--btn-scale-icon" value="1.1" />
                <TokenRow name="--btn-size-icon" value="3.5rem" />
                <TokenRow name="--btn-size-icon-sm" value="3rem" />
                <TokenRow name="--btn-padding-x" value="1.5rem" />
                <TokenRow name="--btn-padding-y" value="1rem" />
                <TokenRow name="--btn-font-size" value="0.9rem" />
                <TokenRow name="--btn-icon-stroke" value="2.5" />
                <TokenRow name="--btn-glow-sm" value="20px" />
                <TokenRow name="--btn-glow-lg" value="40px" />
              </div>
            </Section>

            <Section title="Timing Tokens">
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--duration-fast" value="150ms" />
                <TokenRow name="--duration-normal" value="300ms" />
                <TokenRow name="--duration-slow" value="500ms" />
                <TokenRow name="--duration-slower" value="800ms" />
                <TokenRow name="--theme-transition" value="1s" />
              </div>
            </Section>

            <Section title="Magnetic Tokens">
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--magnetic-strength" value="0.15" />
                <TokenRow name="--magnetic-tilt" value="6" />
              </div>
            </Section>

            <Section title="Spacing Tokens">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Using Tailwind spacing (p-*, gap-*, m-*) for convenience. CSS vars available for edge cases.
              </p>
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--space-xs" value="clamp(0.5rem, 1vw, 0.75rem)" preview={<div className="w-2 h-4 bg-[var(--theme-fg)]" />} />
                <TokenRow name="--space-sm" value="clamp(0.75rem, 2vw, 1.5rem)" preview={<div className="w-4 h-4 bg-[var(--theme-fg)]" />} />
                <TokenRow name="--space-md" value="clamp(1.5rem, 4vw, 2.5rem)" preview={<div className="w-6 h-4 bg-[var(--theme-fg)]" />} />
                <TokenRow name="--space-lg" value="clamp(2.5rem, 6vw, 5rem)" preview={<div className="w-10 h-4 bg-[var(--theme-fg)]" />} />
                <TokenRow name="--space-xl" value="clamp(3rem, 8vw, 6rem)" preview={<div className="w-16 h-4 bg-[var(--theme-fg)]" />} />
              </div>
            </Section>

            <Section title="Border Radius">
              <div className="glass rounded-2xl p-4 no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <TokenRow name="--radius-sm" value="0.5rem" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-sm)" }} />} />
                <TokenRow name="--radius-md" value="0.75rem" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-md)" }} />} />
                <TokenRow name="--radius-lg" value="1rem" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-lg)" }} />} />
                <TokenRow name="--radius-xl" value="1.5rem" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-xl)" }} />} />
                <TokenRow name="--radius-2xl" value="2rem" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-2xl)" }} />} />
                <TokenRow name="--radius-full" value="9999px" preview={<div className="w-24 h-16 bg-[var(--theme-fg)]" style={{ borderRadius: "var(--radius-full)" }} />} />
              </div>
              <p className="text-[rgba(var(--foreground-rgb),0.5)] text-[0.75rem] mt-3 italic">
                Site uses Tailwind classes (rounded-xl, rounded-2xl) which map to these values.
              </p>
            </Section>
          </>
        )}

        {/* Icons Section */}
        {activeTab === "icons" && (
          <>
            <Section title="Icon Library">
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 gap-4">
                {[
                  { icon: <CloseIcon size={24} />, name: "Close" },
                  { icon: <BackIcon size={24} />, name: "Back" },
                  { icon: <ArrowRightIcon size={24} />, name: "ArrowRight" },
                  { icon: <ChevronLeftIcon size={24} />, name: "ChevronLeft" },
                  { icon: <ChevronRightIcon size={24} />, name: "ChevronRight" },
                  { icon: <SunIcon size={24} />, name: "Sun" },
                  { icon: <MoonIcon size={24} />, name: "Moon" },
                  { icon: <LockIcon size={24} />, name: "Lock" },
                  { icon: <UnlockIcon size={24} />, name: "Unlock" },
                ].map((item) => (
                  <div
                    key={item.name}
                    className="glass no-hover p-4 rounded-xl flex flex-col items-center gap-2"
                    style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
                  >
                    <div className="text-[var(--foreground)]">{item.icon}</div>
                    <code className="text-[0.65rem] text-[rgba(var(--foreground-rgb),0.5)]">{item.name}</code>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Icon Sizes">
              <div className="flex items-end gap-6">
                {[
                  { size: 12, label: "2xs" },
                  { size: 16, label: "xs" },
                  { size: 18, label: "sm" },
                  { size: 20, label: "md" },
                  { size: 24, label: "lg" },
                  { size: 32, label: "xl" },
                ].map(({ size, label }) => (
                  <div key={size} className="flex flex-col items-center gap-2">
                    <CloseIcon size={size} />
                    <span className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">{label}</span>
                    <span className="text-[0.6rem] text-[rgba(var(--foreground-rgb),0.3)]">{size}px</span>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}

        {/* Components Section */}
        {activeTab === "components" && (
          <>
            <Section title="StatBlock">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                <StatBlock number={4.8} suffix="★" label="Rating" decimals={1} />
                <StatBlock number={10} suffix="M+" label="Users" />
                <StatBlock number={7} suffix="+" label="Platforms" />
                <StatBlock prefix="#" number={2} label="Feature Request" />
              </div>
            </Section>

            <Section title="MiniCube">
              <Magnetic strength={0.08} tiltStrength={5} padding={320}>
                <MiniCube size={100} color="var(--theme-fg)" isLightMode={isLightMode} />
              </Magnetic>
            </Section>

            <Section title="Magnetic Wrapper">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Any element can be wrapped with Magnetic for cursor attraction.
              </p>
              <div className="flex gap-8">
                <Magnetic strength={0.2} tiltStrength={8}>
                  <div className="glass no-hover p-6 rounded-2xl" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                    <p className="font-medium text-[var(--foreground)]">Strong Magnetic</p>
                    <p className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)]">strength=0.2, tilt=8</p>
                  </div>
                </Magnetic>
                <Magnetic strength={0.05} tiltStrength={3}>
                  <div className="glass no-hover p-6 rounded-2xl" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                    <p className="font-medium text-[var(--foreground)]">Subtle Magnetic</p>
                    <p className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)]">strength=0.05, tilt=3</p>
                  </div>
                </Magnetic>
              </div>
            </Section>

            <Section title="Custom Cursor">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Dot + ring cursor with hover and click states. Colors lerp with theme.
              </p>
              <div className="flex items-center gap-8">
                {/* Default state */}
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-16 h-16 flex items-center justify-center">
                    <div className="absolute w-9 h-9 rounded-full border-2 border-[var(--theme-fg)] opacity-50" />
                    <div className="w-3 h-3 rounded-full bg-[var(--theme-fg)]" />
                  </div>
                  <span className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">Default</span>
                </div>
                {/* Hover state */}
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-16 h-16 flex items-center justify-center">
                    <div className="absolute w-12 h-12 rounded-full border-2 border-[var(--theme-fg)] opacity-70" />
                    <div className="w-4 h-4 rounded-full bg-[var(--theme-fg)]" />
                  </div>
                  <span className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">Hover</span>
                </div>
                {/* Click state */}
                <div className="flex flex-col items-center gap-3">
                  <div className="relative w-16 h-16 flex items-center justify-center">
                    <div className="absolute w-7 h-7 rounded-full border-2 border-[var(--theme-fg)] opacity-90" />
                    <div className="w-2 h-2 rounded-full bg-[var(--theme-fg)]" />
                  </div>
                  <span className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.5)]">Click</span>
                </div>
              </div>
            </Section>

            <Section title="SubNav (Vertical)">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Floating vertical navigation for case study sections. Appears on right edge hover.
              </p>
              <div
                className="glass flex flex-col gap-0.5 px-1.5 py-1.5 rounded-2xl inline-flex"
                style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
              >
                {["Overview", "Challenge", "Solution", "Process", "Results"].map((label, i) => (
                  <button
                    key={label}
                    className="relative px-4 py-2 text-[0.8rem] font-medium rounded-2xl whitespace-nowrap text-center transition-all"
                    style={i === 0 ? {
                      color: "var(--background)",
                      background: `linear-gradient(135deg, var(--theme-base), var(--theme-bright))`,
                    } : {
                      color: "rgba(var(--foreground-rgb), 0.7)",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="ProjectCard">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Interactive card with gradient spotlight on hover. Uses <code className="text-[0.75rem]">.glass-card</code>.
              </p>
              <div
                className="glass-card relative w-full max-w-[320px] overflow-hidden rounded-2xl p-6"
                style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
              >
                <div className="text-[rgba(var(--foreground-rgb),0.5)] text-xs uppercase tracking-[0.15em] font-bold mb-2">Company</div>
                <h3 className="text-[var(--foreground)] text-lg font-bold mb-2">Project Title</h3>
                <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">Brief description of the project and its impact.</p>
                <div className="flex gap-2">
                  <span className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)]">Metric</span>
                </div>
              </div>
            </Section>

            <Section title="Lightbox">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Full-screen image viewer with navigation and captions.
              </p>
              <div className="glass no-hover p-6 rounded-2xl" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-[rgba(var(--foreground-rgb),0.5)]">1 of 5</p>
                  <Button variant="icon-sm" aria-label="Close">
                    <CloseIcon size={18} />
                  </Button>
                </div>
                <div className="bg-[rgba(var(--foreground-rgb),0.1)] rounded-xl aspect-video flex items-center justify-center mb-4">
                  <span className="text-[rgba(var(--foreground-rgb),0.3)]">Image Preview</span>
                </div>
                <p className="text-sm text-center italic text-[rgba(var(--foreground-rgb),0.5)]">Caption text goes here</p>
                <div className="flex justify-center gap-4 mt-4">
                  <Button variant="icon-sm" aria-label="Previous">
                    <ChevronLeftIcon />
                  </Button>
                  <Button variant="icon-sm" aria-label="Next">
                    <ChevronRightIcon />
                  </Button>
                </div>
              </div>
            </Section>

            <Section title="Password Modal">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Pill-shaped password input for protected case studies.
              </p>
              <div className="flex flex-col items-center gap-4">
                <div
                  className="glass flex items-center gap-3 pl-5 pr-2 py-2 rounded-full"
                  style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
                >
                  <LockIcon size={16} className="text-[rgba(var(--foreground-rgb),0.4)]" />
                  <input
                    type="password"
                    placeholder="Enter password"
                    className="bg-transparent text-[var(--foreground)] text-sm outline-none w-32"
                    disabled
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                  <button
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all"
                    style={{
                      background: `linear-gradient(135deg, var(--theme-base), var(--theme-bright))`,
                      color: "white",
                    }}
                  >
                    Unlock
                  </button>
                </div>
                <p className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.4)]">With error state:</p>
                <div
                  className="glass flex items-center gap-3 pl-5 pr-2 py-2 rounded-full"
                  style={{
                    isolation: "isolate",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    boxShadow: "0 0 0 1px rgba(239, 68, 68, 0.5)",
                  }}
                >
                  <LockIcon size={16} className="text-red-400" />
                  <input
                    type="password"
                    placeholder="Incorrect password"
                    className="bg-transparent text-red-400 text-sm outline-none w-32"
                    disabled
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                  />
                  <button
                    className="px-4 py-2 rounded-full text-sm font-medium bg-[rgba(var(--foreground-rgb),0.1)] text-[rgba(var(--foreground-rgb),0.3)]"
                    disabled
                  >
                    Unlock
                  </button>
                </div>
              </div>
            </Section>

            <Section title="Form Inputs">
              <p className="text-[rgba(var(--foreground-rgb),0.6)] text-sm mb-4">
                Input field styles used across the site.
              </p>
              <div className="space-y-4 max-w-[300px]">
                <div>
                  <label className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-[0.1em] mb-2 block">Default</label>
                  <input
                    type="text"
                    placeholder="Placeholder text"
                    className="w-full bg-[rgba(var(--foreground-rgb),0.05)] border border-[rgba(var(--foreground-rgb),0.1)] rounded-xl px-4 py-3 text-sm text-[var(--foreground)] placeholder:text-[rgba(var(--foreground-rgb),0.3)] focus:outline-none focus:border-[var(--theme-fg)]"
                  />
                </div>
                <div>
                  <label className="text-[0.75rem] text-[rgba(var(--foreground-rgb),0.5)] uppercase tracking-[0.1em] mb-2 block">Disabled</label>
                  <input
                    type="text"
                    placeholder="Disabled input"
                    disabled
                    className="w-full bg-[rgba(var(--foreground-rgb),0.02)] border border-[rgba(var(--foreground-rgb),0.05)] rounded-xl px-4 py-3 text-sm text-[rgba(var(--foreground-rgb),0.3)] cursor-not-allowed"
                  />
                </div>
              </div>
            </Section>
          </>
        )}

        {/* Changelog Section */}
        {activeTab === "changelog" && (
          <>
            <Section title="Design System Changelog">
              <div className="space-y-8">
                {/* v1.0.0 */}
                <div className="glass p-6 rounded-2xl no-hover" style={{ isolation: "isolate", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-lg font-bold text-[var(--foreground)] font-headline">v1.0.0</span>
                    <span className="text-[0.7rem] text-[rgba(var(--foreground-rgb),0.4)] font-mono">2026-02-06</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-[var(--theme-fg)] mb-2">Glass System Overhaul</h4>
                      <ul className="text-[0.8rem] text-[rgba(var(--foreground-rgb),0.6)] space-y-1 list-disc list-inside">
                        <li>Consolidated 6 glass tiers to single <code className="text-[0.7rem]">.glass</code> class</li>
                        <li>Added tapered rim light border using conic-gradient</li>
                        <li>Smooth falloff from bright (top) to transparent (bottom)</li>
                        <li>Legacy class names still work as aliases</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[var(--theme-fg)] mb-2">Button Simplification</h4>
                      <ul className="text-[0.8rem] text-[rgba(var(--foreground-rgb),0.6)] space-y-1 list-disc list-inside">
                        <li>Removed <code className="text-[0.7rem]">glass</code> prop from Button component</li>
                        <li>All buttons now use unified glass styling</li>
                        <li>Simplified variant configuration</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[var(--theme-fg)] mb-2">Accessibility</h4>
                      <ul className="text-[0.8rem] text-[rgba(var(--foreground-rgb),0.6)] space-y-1 list-disc list-inside">
                        <li>Improved footer text contrast (WCAG compliance)</li>
                        <li>Added robots.txt for SEO</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* v0.x.x placeholder */}
                <div className="text-center py-8">
                  <p className="text-[0.8rem] text-[rgba(var(--foreground-rgb),0.4)] italic">
                    Earlier versions were not formally tracked.
                  </p>
                </div>
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
